// @ts-nocheck
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  setDoc,
} from "firebase/firestore/lite";
import { useFormik } from "formik";
import type {
  Feature,
  FeatureCollection,
  GeoJsonProperties,
  Geometry,
} from "geojson";
import type React from "react";
import { type FC, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DrawingFeature, Geocoder, Map } from "senf-atomic-design-system";
import { useModals } from "senf-atomic-design-system/src";
import Button from "senf-atomic-design-system/src/components/core/button/Button";
import Dropdown from "senf-atomic-design-system/src/components/core/dropdown/Dropdown";
import {
  convertColorToHSL,
  generateThemeColor,
  hex2hsl,
} from "senf-atomic-design-system/src/styles/helpers";
import { db, generateFirebaseId, useAuthContext } from "senf-shared";
import type { Project } from "senf-shared/types";
import { fetchGisDataFromStorage } from "senf-shared/utils/mapHelpers";
import { v4 as uuidv4 } from "uuid";
import { flyToPolygon } from "../utils/flyToPolygon";
import CesiumControls from "./components/CesiumControls";
import { zoomToAssetById } from "./components/cesium/cesium";
import GisCreateBuilder from "./components/GisCreateBuilder";
import GisUploadBuilder from "./components/GisUploadBuilder";
import Header from "./components/Header";
import PlanningareaBuilder from "./components/PlanningareaBuilder";

interface StyleCondition {
  property: string;
  value: string;
  color: string;
  opacity: number;
}

interface Props {
  mapId?: string;
  handleUpload: (id: string) => void;
  project?: Project;
  setProject: (project: Project) => Promise<void>;
}

interface MapBuilderFormValues {
  title?: string;
  mapId?: string;
  organizationId?: string;
  projectId?: string;
  gisData?: Array<{
    uid: string;
    title: string;
    layerTitle: string;
    layerName: string;
    layerType: string;
    icon: string;
    baseUrl: string;
    data: any;
    type: string;
    legendAttributes: any[];
    selectedAttribute: any;
    selectedTooltip: string;
    withLegend: boolean;
  }>;
  mapbounds?: any;
  mapStyle?: string | null;
  bimModels?: Array<{
    assetId: number;
    assetName: string;
    detectedEpsg: string;
    fileSize: string;
    uploadDate: Date;
    styles?: StyleCondition[];
  }>;
  // Cesium tileset settings
  osmBuildingsEnabled?: boolean;
  osmBuildingColor?: string;
  osmBuildingOpacity?: number;
  imageryStyle?: string;
  createdAt?: string;
  updatedAt?: string;
}

const MapBuilder: FC<Props> = ({
  mapId,
  handleUpload,
  project,
  setProject,
}) => {
  const { t } = useTranslation();
  const { openModal, closeModal } = useModals();
  const mutatableMode = useRef<"edit" | "create" | null>(null);
  const { user } = useAuthContext();
  const [statefulMap, setStatefulMap] = useState<any>(null);
  const [drawMode, setDrawMode] = useState<{
    mode: string | null;
    drawType: string | null;
  }>({ mode: null, drawType: null });
  const currentMapId = mapId || generateFirebaseId();
  const [featureId, setFeatureId] = useState<string | null>(null);
  const [index, setIndex] = useState<string>("planningArea");
  const [customGisdataIndex, setCustomGisdataIndex] = useState("planningArea");
  const [changedStyle, setChangedStyle] = useState<any>(null);
  const [cesiumData, setCesiumData] = useState<any>(null);
  const [isCesiumReady, setIsCesiumReady] = useState(false);

  const initialColor = generateThemeColor(hex2hsl(project?.color || "")).color[
    "400"
  ];

  const formik = useFormik<MapBuilderFormValues>({
    initialValues: {
      title: `${t("global:map")}-${project?.maps?.length || 0 + 1 || 1}`,
      mapId: currentMapId,
      organizationId: project?.organizationId || "",
      projectId: project?.projectId || "",
      mapStyle: null,
      mapbounds: null,
      bimModels: [],
      // Cesium tileset settings - default to false
      osmBuildingsEnabled: false,
      osmBuildingColor: "#ffffff",
      osmBuildingOpacity: 1,
      imageryStyle: "satellite",
      gisData: [
        {
          uid: uuidv4(),
          title: "Custom",
          layerTitle: "Custom",
          layerName: "",
          layerType: "",
          icon: "",
          baseUrl: "",
          data: "",
          type: "geojson",
          legendAttributes: [],
          selectedAttribute: null,
          selectedTooltip: "tooltipLabel",
          withLegend: false,
        },
      ],
    },
    onSubmit: () => {
      // onSubmit is handled by handleSubmit function
    },
  });

  useEffect(() => {
    if (JSON.stringify(formik.values.gisData)) {
      setChangedStyle(false);
      setTimeout(() => {
        setChangedStyle(true);
      }, 10);
    }
  }, [JSON.stringify(formik.values.gisData)]);
  useEffect(() => {
    if (formik.values.gisData[0]?.selectedAttribute) {
      setChangedStyle(false);
      setTimeout(() => {
        setChangedStyle(true);
      }, 10);
    }
  }, [formik.values.gisData[0]?.selectedAttribute]);
  useEffect(() => {
    if (customGisdataIndex) {
      setChangedStyle(false);
      setTimeout(() => {
        setChangedStyle(true);
      }, 10);
    }
  }, [customGisdataIndex]);

  useEffect(() => {
    if (formik.values.gisData[0]?.data === null) {
      setChangedStyle(false);
      setTimeout(() => {
        setChangedStyle(true);
      }, 10);
    }
  }, [formik.values.gisData[0]?.data]);

  useEffect(() => {
    if (mapId) {
      const schemaRef = doc(
        db,
        `organizations/${project?.organizationId}/projects/${project?.projectId}/maps/${mapId}`,
      );
      const fetchMap = async () => {
        const docSnap = await getDoc(schemaRef);

        if (docSnap.exists()) {
          mutatableMode.current = "edit";

          const gisData = await fetchGisDataFromStorage(docSnap.data().gisData);

          formik.setValues({
            title: docSnap.data().title,
            mapbounds: JSON.parse(docSnap.data().mapbounds),
            mapStyle: docSnap.data()?.mapStyle || null,
            gisData,
            bimModels: docSnap.data().bimModels || [],
            // Load Cesium tileset settings with default fallbacks
            osmBuildingsEnabled: docSnap.data().osmBuildingsEnabled || false,
            osmBuildingColor: docSnap.data().osmBuildingColor || "#ffffff",
            osmBuildingOpacity: docSnap.data().osmBuildingOpacity ?? 1,
            imageryStyle: docSnap.data().imageryStyle || "satellite",
            createdAt: docSnap.data().createdAt,
          });

          console.log(formik.values);

          setIndex("create");
          setTimeout(() => {
            setChangedStyle(false);
            setTimeout(() => {
              setChangedStyle(true);
            }, 10);
          }, 1000);
        } else {
          console.log("NOT FOUND");
          mutatableMode.current = "create";
        }
      };

      fetchMap();
    } else {
      mutatableMode.current = "create";
    }
  }, [mapId]);

  // Track if initial zoom has been performed to prevent unwanted auto-zoom during styling
  const initialZoomPerformed = useRef(false);

  useEffect(() => {
    if (
      isCesiumReady &&
      formik.values.mapStyle === "cesium" &&
      formik.values.bimModels &&
      formik.values.bimModels.length > 0 &&
      !initialZoomPerformed.current
    ) {
      // Only zoom on initial load, not when styling or other changes occur
      zoomToAssetById(formik.values.bimModels[0].assetId, 0);
      initialZoomPerformed.current = true;
    }
  }, [isCesiumReady, formik.values.mapStyle, formik.values.bimModels]);

  // Debug Cesium tileset settings changes
  useEffect(() => {
    console.log("🗺️ MapBuilder: Cesium tileset settings changed:", {
      osmBuildingsEnabled: formik.values.osmBuildingsEnabled,
      mapStyle: formik.values.mapStyle,
    });
  }, [formik.values.osmBuildingsEnabled, formik.values.mapStyle]);

  const handleSubmit = async () => {
    console.log("handleSubmit");
    const gisDataString = JSON.stringify(formik.values.gisData);
    const gisDataSize = new Blob([gisDataString]).size;
    const gisDataField = gisDataString;

    if (gisDataSize > 1 * 1024 * 1024) {
      // 1MB in bytes
      try {
        alert(
          "The map is too large. Please minify uploaded GisData to result in a total of max. 1mb",
        );
        // const storage = getStorage();
        // console.log(currentMapId);
        // const path = `organizationsData/${project.organizationId}/projects/${project.projectId}/maps/${currentMapId}/gisData/gisData.json`;
        // const storageRef = ref(storage, path);
        // await uploadString(storageRef, gisDataString);
        // const downloadURL = await getDownloadURL(storageRef);
        // gisDataField = downloadURL;
      } catch (error) {
        console.error("Error uploading gisData to Firebase Storage:", error);
        alert("Failed to upload large gisData. Please try again.");
        return;
      }
    }

    // const filteredGisData = formik.values.gisData
    //   // .slice(1) // This skips the first gisData object
    //   .map((dataItem) => {
    //     const { layers, ...rest } = dataItem;
    //     return rest;
    //   });
    const savedData = {
      organizationId: project?.organizationId,
      projectId: project?.projectId,
      mapId: currentMapId,
      title: formik.values.title,
      mapStyle: formik.values.mapStyle,
      mapbounds: JSON.stringify(formik.values.mapbounds),
      gisData: gisDataField,
      bimModels: formik.values.bimModels || [],
      // Save Cesium tileset settings
      osmBuildingsEnabled: formik.values.osmBuildingsEnabled || false,
      osmBuildingColor: formik.values.osmBuildingColor,
      osmBuildingOpacity: formik.values.osmBuildingOpacity,
      imageryStyle: formik.values.imageryStyle || "satellite",
      createdAt:
        mutatableMode.current === "create"
          ? new Date().toISOString()
          : formik.values.createdAt,
      updatedAt: new Date().toISOString(),
    };
    console.log(savedData);

    // flyToPolygon(statefulMap, formik.values.mapbounds, { duration: 1000 });

    try {
      await setDoc(
        doc(
          collection(
            db,
            `organizations/${project?.organizationId}/projects/${project?.projectId}/maps`,
          ),
          currentMapId,
        ),
        savedData,
      );
      handleUpload(currentMapId);
      console.log(project?.projectId);
      const setProjectPromise = setProject(project?.projectId as any);
      console.log("setProjectPromise");
      if (setProjectPromise instanceof Promise) {
        setProjectPromise.then(() => {
          closeModal();
        });
      } else {
        closeModal();
      }
    } catch (error) {
      throw new Error(error, "Error in add publishsurvey");
    }
  };

  const handleSaveDrawn = (polygon: FeatureCollection) => {
    if (index === "planningArea") {
      if (polygon.features.length < 1) {
        formik.setFieldValue("mapbounds", null);
      } else {
        formik.setFieldValue("mapbounds", polygon);
      }
    } else if (polygon && polygon.features && polygon.features.length > 0) {
      const validFeatures = polygon.features.filter(
        (feature: Feature<Geometry, GeoJsonProperties>) =>
          (feature.geometry as any).coordinates &&
          (feature.geometry as any).coordinates.length > 0,
      );

      let number = 1; // Initialize a counter for the number property

      const updatedFeatures = validFeatures.map(
        (feature: Feature<Geometry, GeoJsonProperties>, index) => {
          // Determine if the datasetIndex is not defined
          console.log(initialColor);
          console.log(
            convertColorToHSL(feature.properties?.color || initialColor),
          );
          const newProperties: any = {
            ...feature.properties,
            // Set datasetIndex only if it's not defined

            datasetIndex: feature.properties?.datasetIndex || 0,
            color: feature.properties?.color || initialColor,
            labelColor: generateThemeColor(
              convertColorToHSL(
                feature.properties?.color || initialColor || [0, 0, 0],
              ) || [0, 0, 0],
            )?.color["800"],
          };

          // Add number property only if datasetIndex was not defined
          if (newProperties.datasetIndex === 0) {
            newProperties.number = number++;
          }

          return {
            ...feature,
            properties: newProperties,
          };
        },
      );

      [...new Array(formik.values.gisData?.length)].map((data, index) => {
        const updatedGeoData = {
          type: "FeatureCollection",
          features: updatedFeatures.filter(
            (feature: Feature<Geometry, GeoJsonProperties>) =>
              feature.properties?.datasetIndex === index,
          ),
        };
        formik.setFieldValue(`gisData[${index}].data`, updatedGeoData);
      });
    }
    setDrawMode({ mode: "draw", drawType: null });
  };

  // useEffect(() => {
  //   setDrawMode({ mode: "draw", drawType: null });
  //   if (statefulMap && statefulMap.df && statefulMap.df) {
  //     console.log(statefulMap.df);
  //     setTimeout(() => {
  //       statefulMap.df.deleteAll();
  //     }, 100);
  //   }
  // }, [index, statefulMap?.df]);

  const handleDelete = async () => {
    const mapRef = doc(
      db,
      `organizations/${project?.organizationId}/projects/${project?.projectId}/maps/${mapId}`,
    );
    await deleteDoc(mapRef);

    setProject(project?.projectId as any).then(() => {
      closeModal();
    });
  };

  const [featureCollection, setFeatureCollection] =
    useState<FeatureCollection | null>(null);

  useEffect(() => {
    if (index === "planningArea") {
      // If index is 0, simply take the mapbounds
      setFeatureCollection(formik.values.mapbounds);
    } else if (index === "create" || index === "upload") {
      setFeatureCollection(null);

      const gisData = formik.values.gisData || [];
      const feat = gisData
        // Filter out any items where data is null or undefined
        .filter((gisItem) => gisItem?.data?.features?.length)
        // Map over the filtered items to extract the features
        .flatMap((gisItem) => gisItem?.data?.features || []); // Flatten the array of features arrays

      // Now feat is a single array containing all features from all the feature collections
      const featureCollectionObject = {
        type: "FeatureCollection",
        features: feat,
      };

      // Set the feature collection if it's not empty
      if (featureCollectionObject.features.length > 0) {
        setFeatureCollection(featureCollectionObject as any);
      }
    }
  }, [
    index,
    JSON.stringify(formik.values.gisData),
    JSON.stringify(formik.values.mapbounds),
  ]);

  const mapStyles = [
    {
      text: "Default Map Style",
      value: null,
      onClick: () => formik.setFieldValue("mapStyle", null),
    },
    {
      text: "White map",
      value: "mapbox://styles/tmorino/cltfrpygw008n01nr0gnhbis7",
      onClick: () =>
        formik.setFieldValue(
          "mapStyle",
          "mapbox://styles/tmorino/cltfrpygw008n01nr0gnhbis7",
        ),
    },
    user?.isSuperAdmin && {
      text: "Cesium",
      value: "cesium",
      onClick: () => formik.setFieldValue("mapStyle", "cesium"),
    },
    // {
    //   text: "Cesium with Rendering",
    //   value: "cesium-rendering",
    //   onClick: () => formik.setFieldValue("mapStyle", "cesium-rendering"),
    // },
  ]?.filter(Boolean);

  const deleteFeature = async (featureId: string) => {
    const featureCollection: FeatureCollection | undefined =
      formik.values.gisData?.[0]?.data;

    if (
      featureCollection &&
      featureCollection.features &&
      featureCollection.features.length > 0
    ) {
      // Filter out the feature with the given ID
      const filteredFeatures = featureCollection.features.filter(
        (feature) => feature.id !== featureId,
      );

      // Return a new FeatureCollection without the deleted feature
      const newFeatureCollection = {
        ...featureCollection,
        features: filteredFeatures,
      };
      formik.setFieldValue(`gisData[${0}].data`, newFeatureCollection);
    }
  };

  const [cameraPosition, setCameraPosition] = useState<{
    center: [number, number];
    height: number;
  } | null>(null);

  const handleCameraMove = (position: {
    center: [number, number];
    height: number;
  }) => {
    setCameraPosition(position);
  };

  const handleDrawComplete = (featureCollection: GeoJSON.FeatureCollection) => {
    // Update the GIS data with the new feature collection
    const newGisData = {
      ...formik.values.gisData?.[0],
      data: featureCollection,
      layerName:
        drawMode.drawType === "polygon" ? "Drawn Polygon" : "Drawn Line",
      layerType: drawMode.drawType === "polygon" ? "polygon" : "line",
    };

    console.log("newGisData", newGisData);

    formik.setFieldValue("gisData", [newGisData]);
  };

  const isDisabled = useMemo(() => {
    if (
      formik.values.mapStyle === "cesium" ||
      formik.values.mapStyle === "cesium-rendering"
    ) {
      return undefined;
    }
    if (!formik.values.title) return t("global:chooseTitle");
    if (
      featureId ||
      !formik.values.mapbounds ||
      !formik.values.mapbounds?.features?.length
    ) {
      return t("global:enterAllRequiredData");
    }
    return undefined;
  }, [
    formik.values.title,
    featureId,
    formik.values.mapbounds,
    formik.values.mapStyle,
    t,
  ]);

  useEffect(() => {
    if (formik.values.mapbounds) {
      flyToPolygon(statefulMap, formik.values.mapbounds, { duration: 2000 });
    }
  }, [formik.values.mapbounds]);

  return (
    <div
      className="flex h-screen w-screen"
      style={{ "--sidebar-width": "545px" } as React.CSSProperties}
    >
      <div className="relative h-screen w-[calc(100vw-var(--sidebar-width))]">
        <div className="l-0 absolute top-0 z-10 m-3 w-96">
          <Geocoder
            statefulMap={statefulMap}
            geolocate={false}
            placeholder={t("util:search", { val: t("global:place") })}
            classNames={{
              inputContainer: "bg-surface-default",
            }}
          />
        </div>

        <Map
          cesiumCameraPosition={cameraPosition}
          handleCesiumCameraMove={handleCameraMove}
          statefulMap={statefulMap}
          setStatefulMap={setStatefulMap}
          flyToMapbounds={false}
          mapbounds={formik.values.mapbounds ? formik.values.mapbounds : null}
          mapStyle={formik.values.mapStyle}
          optimizeString={true}
          legendData={formik.values.gisData}
          bimAssetIds={
            formik.values.bimModels?.map((model) => model.assetId) || []
          }
          bimModels={formik.values.bimModels || []}
          // Pass Cesium imagery settings from formik
          imageryStyle={formik.values.imageryStyle || "satellite"}
          onDrawComplete={handleDrawComplete}
          onCesiumReady={() => setIsCesiumReady(true)}
          // gisData={
          //   index === 1 &&
          //   customGisdataIndex !== "data" &&
          //   formik.values.gisData?.[0] &&
          //   changedStyle
          //     ? formik.values.gisData?.[0]
          //     : false
          // }
        />

        <div className="bottom-2 start-0 h-20 fixed z-50 flex items-center justify-center w-[calc(100vw-var(--sidebar-width))]">
          <DrawingFeature
            mapStyle={formik.values.mapStyle}
            mapType={drawMode}
            statefulMap={statefulMap}
            handleSaveDrawn={handleSaveDrawn as any}
            featureCollection={featureCollection}
            bottom={"100px"}
            featureId={featureId}
            setFeatureId={setFeatureId}
            deleteFeature={deleteFeature}
            openDescription={false}
            // maxFeatures={1}
            shouldTriggerRefresh={[changedStyle, index].toString()}
            // flyToGeoData={formik.values?.mapbounds}
            setDrawMode={setDrawMode}
            allowClickMapToStartDrawing={false}
            // initialDrawType={index === "planningArea" && "mapbounds"}
          />
        </div>

        {/* Cesium Controls - Show when there are bim assets and cesium map style */}
        {(formik.values.mapStyle === "cesium" ||
          formik.values.mapStyle === "cesium-rendering") &&
          formik.values.bimModels?.length && (
            <CesiumControls
              bimModels={formik.values.bimModels}
              onBimModelsChange={(models) =>
                formik.setFieldValue("bimModels", models)
              }
              imageryStyle={formik.values.imageryStyle || "satellite"}
              onImageryStyleChange={(style) => {
                console.log("🔄 MapBuilder: Imagery style changed to:", style);
                formik.setFieldValue("imageryStyle", style);
              }}
              osmBuildingsEnabled={formik.values.osmBuildingsEnabled}
              onOsmBuildingsChange={(enabled) =>
                formik.setFieldValue("osmBuildingsEnabled", enabled)
              }
              osmBuildingColor={formik.values.osmBuildingColor}
              onOsmBuildingColorChange={(color, opacity) => {
                formik.setFieldValue("osmBuildingColor", color);
                formik.setFieldValue("osmBuildingOpacity", opacity);
              }}
              osmBuildingOpacity={formik.values.osmBuildingOpacity}
            />
          )}
      </div>

      <div className="border-border-subdued z-50 flex h-screen w-[var(--sidebar-width)] min-w-[var(--sidebar-width)] flex-col border-l">
        <Header
          title={formik.values.title}
          setTitle={(e) => formik.setFieldValue("title", e.target.value)}
          handleDelete={handleDelete}
          index={index}
          setIndex={setIndex}
          formik={formik}
        />

        {/* {formik.values.mapStyle === "cesium" && (
          <div className="border-border-subdued border-t p-4">
            <h3 className="text-lg font-semibold mb-2">Cesium Map Data</h3>
            <div className="space-y-2">
            {cameraPosition && (
        <div className="">
          <h3 className="font-bold mb-2">Camera Position</h3>
          <p>Center: [{cameraPosition.center[0].toFixed(6)}°, {cameraPosition.center[1].toFixed(6)}°]</p>
          <p>Height: {Math.round(cameraPosition.height)}m</p>
        </div>
      )}

 <Button onClick={() => {
  if (cameraPosition) {
    // Add some random variation to the current position
    const newPosition = {
      center: [
        cameraPosition.center[0] + (Math.random() - 0.5) * 0.1,
        cameraPosition.center[1] + (Math.random() - 0.5) * 0.1
      ] as [number, number],
      height: cameraPosition.height * (0.8 + Math.random() * 0.4) // Random height between 80% and 120% of current
    };
    handleCameraMove(newPosition);
  }
}}
text="Change camera position"
/>  
                 
            
            </div>
          </div>
        )} */}

        <div className="h-full overflow-y-scroll ">
          <div className="flex h-full flex-col gap-3 p-8">
            {index === "planningArea" && (
              <PlanningareaBuilder
                formik={formik}
                setDrawMode={setDrawMode}
                drawMode={drawMode}
                featureId={featureId}
                statefulMap={statefulMap}
              />
            )}
            {index === "upload" && (
              <GisUploadBuilder
                formik={formik}
                setDrawMode={setDrawMode}
                initialColor={initialColor}
              />
            )}
            {index === "create" && (
              <GisCreateBuilder
                formik={formik}
                setDrawMode={setDrawMode}
                drawMode={drawMode}
                featureId={featureId}
                handleSaveDrawn={handleSaveDrawn}
                deleteFeature={deleteFeature}
                initialColor={initialColor}
              />
            )}
          </div>
        </div>
        <div className="border-border-subdued relative  bottom-0 right-0 flex w-full justify-end gap-2 border-t bg-white p-4">
          {/* {index > 0 && (
            <Button
              size="lg"
              onClick={() => setIndex((prev) => prev - 1)}
              leadingIcon="arrowhead-left-md"
            />
          )} */}
          {(user?.isSuperAdmin ||
            project?.organizationId === "OI9COTx1p9FS4gXRrXA0") && (
            <Dropdown
              variant="outline"
              size="lg"
              text={
                mapStyles.find(({ value }) => value === formik.values?.mapStyle)
                  ?.text || "Default Map style"
              }
              hasArrow={false}
              closeOnClick={true}
              items={mapStyles}
            />
          )}
          <Button
            id="submitButton"
            variant="primary"
            size="lg"
            disabled={!!isDisabled}
            disabledNotice={isDisabled}
            onClick={() => {
              if (mutatableMode.current === "create") {
                if (index === "planningArea") {
                  setIndex("upload");
                  return;
                }
                if (index === "upload") {
                  setIndex("create");
                  return;
                }
              }
              if (mutatableMode.current === "edit" || index === "create") {
                handleSubmit();
              }
            }}
            trailingIcon={
              mutatableMode.current === "create" && index !== "create"
                ? "ArrowRight"
                : ""
            }
            text={
              mutatableMode.current === "create" && index !== "create"
                ? t("global:next")
                : t("global:save")
            }
          />
        </div>
      </div>
    </div>
  );
};

export default MapBuilder;
